import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { C } from '../theme';
import { useTheme } from './theme-contexte';
import { dureeDAffichage } from './annonce';
import { api } from '../api/routes';
import { ErreurApi } from '../api/client';
import { messageDErreur } from '../api/useDonnees';
import {
  SESSION_PERDUE,
  accueilDejaVu,
  effacerSession,
  lireJeton,
  lireUtilisateur,
  marquerAccueilVu,
  prenomDe,
} from '../session';
import type { ElementBoite, Utilisateur } from '../api/types';
import { masquerVoile } from '../voile';
import { EcranLancement, Panne } from './composants/base';
import { BarreNav, type Ecran } from './composants/BarreNav';
import { PanneauOrganiser } from './composants/PanneauOrganiser';
import { Connexion } from './ecrans/Connexion';
import { Inscription } from './ecrans/Inscription';
import { CreerFoyer } from './ecrans/CreerFoyer';
import { Onboarding } from './ecrans/Onboarding';
import { Aujourdhui } from './ecrans/Aujourdhui';
import { Boite } from './ecrans/Boite';
import { Compris } from './ecrans/Compris';
import { Planning } from './ecrans/Planning';
import { Reglages } from './ecrans/Reglages';

/**
 * Fuseau de repli, utilise seulement tant que le foyer n'a pas pu etre lu. Le vrai fuseau
 * appartient au FOYER (le premier testeur est a Dakar, pas en France) : il n'est jamais
 * ecrit en dur ailleurs.
 */
const FUSEAU_DE_REPLI = 'UTC';

type Phase = 'verification' | 'connexion' | 'inscription' | 'creer-foyer' | 'accueil' | 'app';

export function App() {
  const [phase, setPhase] = useState<Phase>('verification');
  const [utilisateur, setUtilisateur] = useState<Utilisateur | null>(lireUtilisateur);
  const [fuseau, setFuseau] = useState(FUSEAU_DE_REPLI);
  const [panneDemarrage, setPanneDemarrage] = useState<string | null>(null);
  // Le theme suit le fuseau du FOYER : tant qu'il n'est pas connu, `FUSEAU_DE_REPLI`
  // sert de demarrage, et l'apparence se corrige des que le foyer repond.
  const theme = useTheme(fuseau);

  const [ecran, setEcran] = useState<Ecran>('aujourdhui');
  // Seul l'identifiant est retenu : l'ecran recharge l'element lui-meme, de facon a voir
  // la question suivante apparaitre apres chaque reponse.
  const [comprisId, setComprisId] = useState<string | null>(null);
  const [panneauOuvert, setPanneauOuvert] = useState(false);
  const [message, setMessage] = useState('');
  // Incremente apres chaque ecriture : les ecrans qui en dependent se rechargent.
  const [version, setVersion] = useState(0);

  // Ce qui attend un geste dans la Boite, pour la pastille de l'onglet. Lu UNE fois par
  // rafraichissement, depuis n'importe quel ecran : sans cette pastille, une question de
  // Colette reste invisible tant qu'on n'ouvre pas la Boite — exactement le probleme
  // remonte par Marianne. Requete de base, aucun appel d'IA.
  const [aValider, setAValider] = useState(0);
  useEffect(() => {
    if (phase !== 'app') return;
    let vivant = true;
    api
      .propositions()
      .then(({ proposals }) => {
        if (vivant) setAValider(proposals.filter((p) => p.status === 'PROPOSED').length);
      })
      .catch(() => {
        // Une pastille absente vaut mieux qu'un ecran en erreur : elle n'est qu'un rappel.
      });
    return () => {
      vivant = false;
    };
  }, [phase, version]);

  const minuteurMessage = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(minuteurMessage.current), []);

  const signaler = useCallback((texte: string) => {
    setMessage(texte);
    window.clearTimeout(minuteurMessage.current);
    // La duree suit la LONGUEUR : le refus d'une demande tient en trois phrases, et il
    // doit pouvoir se lire (voir annonce.ts).
    minuteurMessage.current = window.setTimeout(() => setMessage(''), dureeDAffichage(texte));
  }, []);

  const fermerLeMessage = useCallback(() => {
    window.clearTimeout(minuteurMessage.current);
    setMessage('');
  }, []);

  const rafraichir = useCallback(() => setVersion((n) => n + 1), []);

  const demarrer = useCallback(async () => {
    setPanneDemarrage(null);
    if (lireJeton() === null) {
      setPhase('connexion');
      return;
    }
    try {
      // Le jeton est revalide au demarrage : conserve depuis des semaines, il peut avoir
      // ete revoque. Mieux vaut le decouvrir ici que devant des ecrans vides.
      const { user } = await api.moi();
      setUtilisateur(user);
      const { household } = await api.foyer();
      setFuseau(household.timezone);
      setPhase(accueilDejaVu() ? 'app' : 'accueil');
    } catch (cause) {
      if (cause instanceof ErreurApi && cause.statut === 401) {
        effacerSession();
        setUtilisateur(null);
        setPhase('connexion');
        return;
      }
      if (cause instanceof ErreurApi && cause.statut === 404) {
        // Compte valide mais rattache a aucun foyer : c'est l'etat normal juste apres une
        // inscription. On propose de creer le foyer au lieu de laisser l'ecran vide.
        setPhase('creer-foyer');
        return;
      }
      setPanneDemarrage(messageDErreur(cause));
    }
  }, []);

  useEffect(() => {
    void demarrer();
  }, [demarrer]);

  // Le voile de lancement (index.html) reste affiche pendant TOUTE la verification de
  // session : il ne s'efface que lorsque l'application sait ou elle en est. L'utilisateur
  // voit « Colette » qui respire, jamais un ecran a moitie rendu.
  //
  // Une PANNE au demarrage est aussi « savoir ou l'on en est ». Sans cette seconde
  // condition, le voile restait par-dessus le message « Pas de connexion » et son bouton
  // « Réessayer » : sans reseau au lancement, la seule issue etait de fermer
  // l'application (audit du 2026-09-27).
  useEffect(() => {
    if (phase !== 'verification' || panneDemarrage !== null) masquerVoile();
  }, [phase, panneDemarrage]);

  // Ce que l'ecran de connexion doit DIRE en s'ouvrant. Un retour muet a la connexion,
  // au milieu d'un geste, se lirait comme une panne.
  const [annonceConnexion, setAnnonceConnexion] = useState<string | null>(null);

  // Le serveur a refuse le jeton en cours d'usage (expire au bout de trente jours, ou
  // revoque par un changement de mot de passe fait ailleurs) : `api/client.ts` a deja
  // efface la session, il reste a ramener a la connexion.
  useEffect(() => {
    const revenirALaConnexion = () => {
      setUtilisateur(null);
      setComprisId(null);
      setPanneauOuvert(false);
      setEcran('aujourdhui');
      setPanneDemarrage(null);
      setAnnonceConnexion('Ta session a expiré. Reconnecte-toi pour retrouver ton foyer.');
      setPhase('connexion');
    };
    window.addEventListener(SESSION_PERDUE, revenirALaConnexion);
    return () => window.removeEventListener(SESSION_PERDUE, revenirALaConnexion);
  }, []);

  const seDeconnecter = () => {
    effacerSession();
    setUtilisateur(null);
    setComprisId(null);
    setEcran('aujourdhui');
    setPhase('connexion');
  };

  const ouvrirCompris = (element: ElementBoite) => {
    setPanneauOuvert(false);
    setComprisId(element.id);
    rafraichir();
  };

  let contenu: ReactNode = null;

  if (panneDemarrage !== null) {
    contenu = <Panne message={panneDemarrage} onReessayer={() => void demarrer()} />;
  } else if (phase === 'verification') {
    contenu = <EcranLancement />;
  } else if (phase === 'connexion') {
    contenu = (
      <Connexion
        annonce={annonceConnexion}
        onConnecte={() => {
          // L'annonce a servi des que la connexion reussit. L'effacer ICI, et nulle part
          // ailleurs : tout retour ulterieur a cet ecran (une deconnexion voulue, par
          // exemple) part ainsi d'une annonce vide, sans que chaque chemin ait a y penser.
          setAnnonceConnexion(null);
          setPhase('verification');
          void demarrer();
        }}
        versInscription={() => setPhase('inscription')}
      />
    );
  } else if (phase === 'inscription') {
    contenu = (
      <Inscription
        onInscrit={() => {
          // Le compte existe, le foyer pas encore : `demarrer` recevra un 404 sur
          // /foyers/moi et enchainera de lui-meme sur la creation du foyer.
          setPhase('verification');
          void demarrer();
        }}
        versConnexion={() => setPhase('connexion')}
      />
    );
  } else if (phase === 'creer-foyer') {
    contenu = (
      <CreerFoyer
        nomUtilisateur={utilisateur?.displayName ?? ''}
        onCree={() => {
          setPhase('verification');
          void demarrer();
        }}
        onDeconnexion={seDeconnecter}
      />
    );
  } else if (phase === 'accueil') {
    contenu = (
      <Onboarding
        termine={() => {
          marquerAccueilVu();
          setPhase('app');
        }}
      />
    );
  } else if (utilisateur !== null) {
    contenu = (
      <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {comprisId !== null ? (
          <Compris
            elementId={comprisId}
            utilisateurId={utilisateur.id}
            version={version}
            retour={() => {
              setComprisId(null);
              rafraichir();
            }}
            signaler={signaler}
            rafraichir={rafraichir}
          />
        ) : (
          <>
            {ecran === 'aujourdhui' ? (
              <Aujourdhui
                prenom={prenomDe(utilisateur)}
                fuseau={fuseau}
                version={version}
                signaler={signaler}
                rafraichir={rafraichir}
                theme={theme}
                ouvrirPlanning={() => setEcran('planning')}
              />
            ) : null}
            {ecran === 'boite' ? (
              <Boite
                version={version}
                ouvrirPanneau={() => setPanneauOuvert(true)}
                ouvrirCompris={ouvrirCompris}
                signaler={signaler}
                rafraichir={rafraichir}
              />
            ) : null}
            {ecran === 'planning' ? (
              <Planning
                fuseau={fuseau}
                utilisateurId={utilisateur.id}
                version={version}
                signaler={signaler}
                rafraichir={rafraichir}
                ouvrirCompris={(elementId) => {
                  setComprisId(elementId);
                  rafraichir();
                }}
              />
            ) : null}
            {ecran === 'reglages' ? (
              <Reglages
                utilisateur={utilisateur}
                version={version}
                onDeconnexion={seDeconnecter}
                signaler={signaler}
                rafraichir={rafraichir}
                theme={theme}
              />
            ) : null}
          </>
        )}

        <BarreNav
          ecran={comprisId === null ? ecran : 'boite'}
          aller={(destination) => {
            setComprisId(null);
            setEcran(destination);
          }}
          ouvrirPanneau={() => setPanneauOuvert(true)}
          aValider={aValider}
        />
      </div>
    );
  }

  return (
    <div className="scene">
      <div className="cadre">
        {contenu}

        {panneauOuvert ? (
          <PanneauOrganiser
            fermer={() => setPanneauOuvert(false)}
            onOrganise={ouvrirCompris}
            signaler={signaler}
          />
        ) : null}

        {message !== '' ? (
          <div
            className="apparition"
            role="status"
            // Un long message reste longtemps : un appui le ferme sans attendre.
            onClick={fermerLeMessage}
            style={{
              position: 'absolute',
              left: 20,
              right: 20,
              bottom: 108,
              background: C.bandeau,
              color: '#FFFFFF',
              borderRadius: 12,
              padding: '12px 16px',
              fontSize: 14,
              fontWeight: 700,
              textAlign: 'center',
              zIndex: 20,
            }}
          >
            {message}
          </div>
        ) : null}
      </div>
    </div>
  );
}

